// Promo de contado (Diego, 2-nov-2026): un botón en la cotización sube el descuento de contado de 15 % a 20 % y la promo sale en
// todo el PDF. Las fechas y el porcentaje viven SOLO aquí: para otra promo o para mover una fecha, se cambia esta tabla.
// Regla: 20 % de contado hasta el 31-dic-2026. Diego, 3-nov-2026 (~04:00): «Promo Buen Fin» DESDE HOY, del 3 al 17-nov, con esa fecha
// límite (antes solo del 13 al 17); del 18-nov al 31-dic, «Promo de fin de año», sin mencionar Buen Fin ni llevar su logo. Buen Fin va
// primero: dentro de su ventana, el botón ofrece esa. Al cliente se le dice SOLO la promo: «20 % de descuento de contado, o 18 meses sin
// intereses con tarjeta de crédito» (nunca el 15 % de siempre, ni «de 15 % a 20 %», ni «en lugar de $…»).
import { z } from 'zod';
import { MSI_TOPE_LISTA_CON_IVA } from '../constantes.ts';
import { fechaHermosillo } from '../tiempo.ts';

export const PROMO_PCT = 20;
export const PROMOS = Object.freeze([
  { id: 'buen-fin-2026', etiqueta: 'Promo Buen Fin', nombre: 'Buen Fin', desde: '2026-11-03', hasta: '2026-11-17' },
  { id: 'fin-de-ano-2026', etiqueta: 'Promo de fin de año', nombre: 'de fin de año', desde: '2026-11-01', hasta: '2026-12-31' },
] as const);

/** Diego, 3-nov-2026: la promo trae también 18 meses sin intereses con tarjeta de crédito, SOLO en casa (formato residencial), sobre la
 *  lista y con el mismo tope que los 3, 6 y 12 de siempre (lista con IVA de hasta MSI_TOPE_LISTA_CON_IVA). Sin promo no hay 18. */
export const PROMO_MSI_MESES = 18;

/** La mensualidad de los 18 MSI de la promo para una opción de casa con esta lista CON IVA (lista ÷ 18, sin redondear: el PDF la imprime
 *  con centavos), o null si la lista pasa del tope (ahí no hay meses sin intereses). Quien llama decide si hay promo y si es casa. */
export function mensualidadMsiPromo(lista: number): number | null {
  return Number.isFinite(lista) && lista > 0 && lista <= MSI_TOPE_LISTA_CON_IVA ? lista / PROMO_MSI_MESES : null;
}

/** ¿Lleva SEGURO los 18 MSI el PDF de casa con la promo cuyo contado CON IVA (el de la recomendada) es este? Para quien no ve la lista: el
 *  WhatsApp de una versión solo guarda su contado (ResumenVersion). Con la promo el contado es cuando mucho el 80 % de la lista más 50
 *  centavos de redondeo (un adicional sin descuento pesa MÁS en el contado: 85 % de su lista), así que un contado hasta el 80 % del tope menos
 *  $100 asegura una lista dentro del tope. Arriba de eso el PDF quizá sí los lleve, pero el mensaje no los promete: nunca de más. */
export function msiPromoSeguroPorContado(contado: number): boolean {
  return Number.isFinite(contado) && contado > 0 && contado <= MSI_TOPE_LISTA_CON_IVA * (1 - PROMO_PCT / 100) - 100;
}

/** Último día con alguna promo: hasta aquí llega el 20 % de contado (el mensaje de «Hoy toca» lo dice con esta fecha). */
export const PROMO_HASTA: string = PROMOS.reduce<string>((x, p) => (p.hasta > x ? p.hasta : x), PROMOS[0]!.hasta);

/** La promo CONGELADA en la cotización: un folio con promo no cambia solo cuando pasa la fecha. */
export const PromoCotizacion = z.strictObject({
  id: z.string().min(1).max(40),
  nombre: z.string().min(1).max(40),
  pct: z.number().min(1).max(30),
  vence: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type PromoCotizacion = z.infer<typeof PromoCotizacion>;

/** La promo que el botón ofrece hoy (fecha AAAA-MM-DD en hora de Hermosillo); null = fuera de toda fecha, sin botón. */
export function promoVigente(hoy: string): (typeof PROMOS)[number] | null {
  return PROMOS.find((p) => p.desde <= hoy && hoy <= p.hasta) ?? null;
}

/** Lo que el botón guarda en parametros.promo: su fecha límite es la de ESA promo (Buen Fin, 17-nov; fin de año, 31-dic). */
export function promoDeHoy(hoy: string): PromoCotizacion | null {
  const p = promoVigente(hoy);
  return p ? { id: p.id, nombre: p.nombre, pct: PROMO_PCT, vence: p.hasta } : null;
}

/** Por qué esta cotización no lleva promo (el mismo motivo en pantalla y en el motor); null = sí aplica. El 20 % es de sistemas
 *  solares de contado: baterías y equipos sueltos ya van a su margen, y el precio único no tiene lista ni descuento. */
export function motivoSinPromo(b: { cuestionario: { tipo?: string | undefined; respaldo?: unknown; recorte_punta?: unknown }; parametros?: { recorte_punta?: unknown } }): string | null {
  if (b.cuestionario.tipo === 'aislado') return 'un sistema aislado';
  if (b.cuestionario.tipo === 'baterias' || b.cuestionario.tipo === 'ampliacion_baterias' || b.cuestionario.respaldo) return 'un sistema con baterías';
  if (b.cuestionario.recorte_punta || b.parametros?.recorte_punta) return 'un recorte de punta';
  return null;
}

/** Cómo sale HOY una promo ya puesta (en pantalla, en el PDF y en WhatsApp): el descuento no cambia, pero «Buen Fin» no se dice fuera
 *  de su ventana (3-nov: una de fin de año puesta el 1 o el 2-nov se queda de fin de año aunque hoy el botón ofrezca Buen Fin). Un folio de Buen Fin que se vuelve a sacar después del 17-nov sale como la promo que sigue, con SU fecha límite. Pasado
 *  el 31-dic también sale con la última: el aviso rojo del motor dice que venció. Una de fin de año no cambia dentro del Buen Fin.
 *  El nombre y la fecha límite salen SIEMPRE de esta tabla, nunca de lo congelado: si Diego mueve una fecha aquí, los folios que ya la
 *  tienen la siguen cobrando con la fecha nueva (verificación del 2-nov, ronda 3: con la fecha congelada quedaban en rojo y sin PDF). */
export function promoAlDia(promo: PromoCotizacion, hoy: string): PromoCotizacion {
  const suya = PROMOS.find((p) => p.id === promo.id);
  if (!suya) return promo;
  const es = suya.desde <= hoy && hoy <= suya.hasta ? suya : (promoVigente(hoy) ?? PROMOS[PROMOS.length - 1]!);
  return { ...promo, id: es.id, nombre: es.nombre, vence: es.hasta };
}

/** La promo con la que salió el PDF de una versión: la congelada en SU contenido, y solo si lo que cobra (la opción RECOMENDADA) la lleva. Es la
 *  que repite el WhatsApp de esa versión (no la del borrador de hoy, que se pudo quitar y volver a poner con otro nombre y otra fecha). Con un
 *  precio escrito a mano en la recomendada el PDF no anuncia la promo aunque otra opción la lleve (verificación del 2-nov, ronda 2). */
export function promoDeVersion(contenido: { parametros: { promo?: PromoCotizacion | null | undefined }; resultado: { recomendada: string; opciones: readonly { id: string; precio: { promo?: unknown } }[] } | null }): PromoCotizacion | null {
  const promo = contenido.parametros.promo ?? null;
  const r = contenido.resultado;
  return promo && r?.opciones.find((o) => o.id === r.recomendada)?.precio.promo ? promo : null;
}

/** Por qué una promo guardada NO es una de esta tabla (otro id u otro porcentaje), o null si sí es. Solo el botón pone la promo; una que
 *  llegó por otro camino (una versión de antes que la aceptaba sin revisar, una petición a mano) no se cobra: el motor la marca en rojo
 *  (verificación del 2-nov, ronda 2: una «promo» de 30 % se cobraba al volver a publicar). La fecha y el nombre congelados no cuentan: se
 *  leen de la tabla (promoAlDia), así que mover una fecha aquí no deja «desconocidas» a las ya puestas (ronda 3). */
export function promoDesconocida(promo: PromoCotizacion): string | null {
  const suya = PROMOS.find((p) => p.id === promo.id);
  if (!suya) return `«${promo.nombre}» no es una promo del cotizador`;
  if (promo.pct !== PROMO_PCT) return `la promo guardada dice ${promo.pct} % y la ${suya.etiqueta} es de ${PROMO_PCT} %`;
  return null;
}

/** Hoy en Hermosillo, para la pantalla (la conversión vive en tiempo.ts, como toda). */
export function hoyEnHermosillo(): string {
  return fechaHermosillo(new Date());
}
